// Forgiving product search, shared by the home page (whole shop) and the category pages.
//
// Every word typed must match something about the product. A word matches a word in the
// product's name, its category or cut, or its code when it is:
//   - the same word, ignoring case, accents and punctuation ("cote" finds "Côte de Boeuf");
//   - the same apart from a plural ending ("turkeys", "chops");
//   - the start of a word, for searching while typing ("chipo");
//   - one letter out for words of four to seven letters, two for longer ones ("turky", "venisn");
//   - a kind of the thing searched for, from the short list below ("cheese" finds the cheddars).
// Small words such as "of" and "with" are ignored unless they are all that was typed.
//
// Products that match only through their description are kept apart, as "also mentioned":
// a description often names other products ("lovely with cheese" on a salami), so mixing them
// in with real matches would mislead.

// Words a customer may search for that the product names do not use. Each word on the left
// also matches the words on the right.
const KINDS = {
    cheese: ['cheddar', 'brie', 'stilton', 'camembert', 'comte', 'gouda', 'bigod', 'tunworth', 'baron'],
    sausage: ['chipolata', 'banger', 'chorizo', 'salami'],
    ham: ['gammon'],
    fizz: ['champagne', 'prosecco', 'cava', 'cremant', 'sparkling'],
    bubbly: ['champagne', 'prosecco', 'cava', 'cremant', 'sparkling'],
    voucher: ['gift card', 'giftcard'],
};

const SMALL_WORDS = new Set(['a', 'an', 'and', 'the', 'of', 'with', 'in', 'on', 'for', 'to']);

function normalise(text) {
    return String(text == null ? '' : text)
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

// Plural endings off, so "turkeys" and "turkey", "pies" and "pie", "boxes" and "box" meet.
function stem(word) {
    if (word.length > 4 && word.endsWith('ies')) return word.slice(0, -3) + 'y';
    if (word.length > 4 && /(s|x|z|ch|sh)es$/.test(word)) return word.slice(0, -2);
    if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
    return word;
}

function words(text) {
    return normalise(text).split(' ').filter(Boolean).map(stem);
}

// How many letters may be wrong in a word this long.
function allowedSlips(word) {
    if (word.length >= 8) return 2;
    if (word.length >= 4) return 1;
    return 0;
}

// Letters added, removed, changed or swapped with a neighbour (optimal string alignment).
// Gives up early once the answer is above `limit`.
function slips(a, b, limit) {
    if (Math.abs(a.length - b.length) > limit) return limit + 1;
    let prev2 = null;
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        const row = [i];
        let best = i;
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let v = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
            if (prev2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                v = Math.min(v, prev2[j - 2] + 1);
            }
            row.push(v);
            if (v < best) best = v;
        }
        if (best > limit) return limit + 1;
        prev2 = prev;
        prev = row;
    }
    return prev[b.length];
}

// How well one typed word matches a list of product words: 0 for no match, up to 3 for exact.
function wordScore(typed, productWords, forgiving) {
    let best = 0;
    for (const w of productWords) {
        if (w === typed) return 3;
        if (typed.length >= 2 && w.startsWith(typed)) best = Math.max(best, 2.5);
        if (!forgiving) continue;
        const limit = allowedSlips(typed);
        if (limit === 0) continue;
        if (slips(typed, w, limit) <= limit) best = Math.max(best, 2);
        else if (w.length > typed.length && slips(typed, w.slice(0, typed.length), limit) <= limit) {
            best = Math.max(best, 1.5);
        }
    }
    return best;
}

// The typed word and the kinds of thing it covers, in the same form as product words.
function alternatives(typed) {
    const kinds = KINDS[typed] || [];
    return [typed, ...kinds.map(k => words(k).join(' '))];
}

function scoreAgainst(typed, fields, forgiving) {
    let best = 0;
    for (const alt of alternatives(typed)) {
        const altWords = alt.split(' ');
        for (const { words: fieldWords, weight } of fields) {
            // Kinds match exactly or by their start only, never with slips ("baron" is one letter
            // from "bacon"). A kind given as two words ("gift card") must match both.
            const s = Math.min(...altWords.map(a => wordScore(a, fieldWords, forgiving && alt === typed)));
            if (s > 0) best = Math.max(best, s * weight * (alt === typed ? 1 : 0.9));
        }
    }
    return best;
}

/**
 * Prepares products for searching. `describe(product)` returns the category and cut names to
 * search alongside the product's own name, code and description.
 */
export function indexProducts(products, describe = () => []) {
    return products.map(product => ({
        product,
        fields: [
            { words: words(product.name), weight: 3 },
            { words: words(describe(product).join(' ')), weight: 2 },
            { words: words(String(product.sku || '').replace(/-/g, ' ')), weight: 2 },
        ],
        description: [{ words: words(product.description), weight: 1 }],
    }));
}

/**
 * Searches prepared products. Returns `{ matches, alsoMentioned }`, each a list of products,
 * best match first.
 */
export function searchProducts(index, query) {
    let typed = words(query);
    const meaningful = typed.filter(w => !SMALL_WORDS.has(w));
    if (meaningful.length > 0) typed = meaningful;
    if (typed.length === 0) return { matches: [], alsoMentioned: [] };

    const matches = [];
    const alsoMentioned = [];
    for (const entry of index) {
        let score = 0;
        let viaDescription = false;
        let matched = true;
        for (const t of typed) {
            const s = scoreAgainst(t, entry.fields, true);
            if (s > 0) {
                score += s;
                continue;
            }
            // Descriptions only by whole word or the start of one: no allowance for slips there.
            const d = scoreAgainst(t, entry.description, false);
            if (d > 0) {
                score += d;
                viaDescription = true;
                continue;
            }
            matched = false;
            break;
        }
        if (!matched) continue;
        (viaDescription ? alsoMentioned : matches).push({ product: entry.product, score });
    }
    const byScore = (a, b) => b.score - a.score || String(a.product.name).localeCompare(String(b.product.name));
    return {
        matches: matches.sort(byScore).map(m => m.product),
        alsoMentioned: alsoMentioned.sort(byScore).map(m => m.product),
    };
}
